import React, { useState, useEffect, useRef } from 'react';
import { 
  LogIn, LogOut, Package, Search, Plus, Edit2, Trash2, 
  Star, Save, X, Image, Upload, Settings, Layout, 
  AlertCircle, CheckCircle, Loader, Home, ShoppingBag,
  Type, Link as LinkIcon, Smartphone, RefreshCw, Sparkles,
  Instagram, Facebook, Phone, Mail, MapPin, Gift, Copy, Check,
  BarChart3, TrendingUp, Eye, Users, Monitor, Smartphone as MobileIcon,
  Files, Palette
} from 'lucide-react';
import ProductVariantsEditor from '../components/ProductVariantsEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Navigation
  const [activeTab, setActiveTab] = useState('produtos');

  // Data State
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [upsells, setUpsells] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState('');

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  // Upload
  const [uploadLoading, setUploadLoading] = useState(false);
  const [variantUploadLoading, setVariantUploadLoading] = useState({});
  const [heroUploadLoading, setHeroUploadLoading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const fileInputRef = useRef(null);
  const heroFileInputRef = useRef(null);
  const variantFileInputRefs = useRef({});
  
  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);

  // Duplicate product
  const [duplicatingId, setDuplicatingId] = useState(null);

  // Favicon
  const [faviconUrl, setFaviconUrl] = useState('');
  const [faviconUploadLoading, setFaviconUploadLoading] = useState(false);
  const faviconFileInputRef = useRef(null);

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({ show: false, type: '', id: '', title: '' });
  const [deleting, setDeleting] = useState(false);

  // Check session on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) verifyToken(token);
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAuthToken(token);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });

      if (response.ok) {
        const data = await response.json();
        setAuthToken(data.access_token);
        setIsAuthenticated(true);
        localStorage.setItem('admin_token', data.access_token);
      } else if (response.status === 429) {
        setLoginError('Muitas tentativas. Aguarde alguns minutos.');
      } else {
        setLoginError('Credenciais inválidas.');
      }
    } catch (error) {
      setLoginError('Erro de conexão.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthToken('');
    localStorage.removeItem('admin_token');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${authToken}` };
      
      const [productsRes, settingsRes, upsellsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/products`, { headers }),
        fetch(`${API_URL}/api/admin/site-settings`, { headers }),
        fetch(`${API_URL}/api/admin/upsells`, { headers }),
        fetch(`${API_URL}/api/admin/categories`, { headers })
      ]);

      if (productsRes.ok) setProducts(await productsRes.json());
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        // Transform to match old format for compatibility
        setSettings({
          hero: {
            badge: settingsData.hero_badge,
            title: settingsData.hero_title,
            subtitle: settingsData.hero_subtitle,
            image_url: settingsData.hero_image_url,
            cta_text: settingsData.hero_cta_text,
            cta_link: settingsData.hero_cta_link,
          },
          top_bar: {
            enabled: settingsData.top_notice_enabled,
            message: settingsData.top_notice_text,
          },
          logo: {
            url: settingsData.logo_url,
          },
          footer: {
            about_text: settingsData.footer_about_text,
            phone: settingsData.contact_phone,
            email: settingsData.contact_email,
            location: settingsData.contact_city,
            whatsapp: settingsData.whatsapp_number,
            instagram: settingsData.instagram_url,
            facebook: settingsData.facebook_url,
          },
          editable_section: {
            is_active: settingsData.home_after_brands_enabled,
            title: settingsData.home_after_brands_title,
            text: settingsData.home_after_brands_text,
          },
          brands_section: {
            title: "Marcas Consolidadas",
            subtitle: "Encontre seu próximo smartphone pela marca",
            items: []
          },
          _raw: settingsData
        });
        // Carrega favicon
        if (settingsData.favicon_url) {
          setFaviconUrl(settingsData.favicon_url);
        }
      }
      if (upsellsRes.ok) setUpsells(await upsellsRes.json());
      if (categoriesRes.ok) {
        const cats = await categoriesRes.json();
        // Update brands_section items
        setSettings(prev => ({
          ...prev,
          brands_section: {
            ...prev?.brands_section,
            items: cats.map(c => ({
              key: c.slug,
              label: c.name,
              description: c.description,
              icon: c.icon_name,
              color: c.icon_color,
              order: c.sort_order,
              is_active: c.is_active,
              id: c.id
            }))
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Analytics
  const fetchAnalytics = async (days = 30) => {
    setAnalyticsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/summary?days=${days}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Fetch analytics when tab changes to analytics
  useEffect(() => {
    if (activeTab === 'analytics' && !analytics) {
      fetchAnalytics(analyticsPeriod);
    }
  }, [activeTab]);

  // ========== API CALLS ==========

  const saveSettings = async (section, data) => {
    setSaveLoading(true);
    setSaveMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/admin/settings/${section}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setSaveMessage({ type: 'success', text: 'Salvo com sucesso!' });
        fetchAllData();
        setTimeout(() => setShowModal(false), 1000);
      } else {
        setSaveMessage({ type: 'error', text: 'Erro ao salvar' });
      }
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Erro de conexão' });
    } finally {
      setSaveLoading(false);
    }
  };

  // Ref para controlar múltiplos cliques
  const saveInProgressRef = useRef(false);
  const lastRequestIdRef = useRef(null);

  const saveProduct = async () => {
    // Proteção contra múltiplos cliques
    if (saveInProgressRef.current) {
      console.log('[SAVE PRODUCT] Requisição já em andamento, ignorando clique');
      return;
    }
    
    saveInProgressRef.current = true;
    setSaveLoading(true);
    setSaveMessage({ type: '', text: '' });

    // Validações
    if (!formData.brand && !formData.title) {
      setSaveMessage({ type: 'error', text: 'Preencha o título ou marca do produto' });
      setSaveLoading(false);
      saveInProgressRef.current = false;
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      setSaveMessage({ type: 'error', text: 'Preencha o preço do produto' });
      setSaveLoading(false);
      saveInProgressRef.current = false;
      return;
    }

    // Gerar request ID único para idempotência
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    lastRequestIdRef.current = requestId;

    try {
      // Transform form data to new API format
      const productData = {
        title: formData.title || `${formData.brand} ${formData.model}`,
        brand_slug: formData.brand?.toLowerCase() || formData.brand_slug || 'outros',
        model: formData.model || '',
        description: formData.description || '',
        price_pix: parseFloat(formData.price) || 0,
        price_card: formData.market_price ? parseFloat(formData.market_price) : null,
        image_url: formData.images?.[0] || '',
        images: formData.images || [],
        is_featured: formData.featured || formData.is_featured || false,
        featured_order: formData.featured_order || 0,
        is_active: formData.is_active !== false,
        stock_status: formData.stock > 0 ? 'pronta entrega' : 'esgotado',
        condition: formData.condition === 'new' ? 'novo' : 'seminovo',
        storage: formData.storage || '',
        // NOVO SISTEMA SIMPLIFICADO
        storage_variants: formData.storage_variants || [],
        color_variants: (formData.color_variants || []).map(cv => ({
          ...cv,
          // Limpa _stableKey antes de enviar (é só para o frontend)
          _stableKey: undefined
        })),
        enable_color_price_adjust: formData.enable_color_price_adjust || false,
        video_url: formData.video_url || ''
      };

      console.log('[SAVE PRODUCT] Enviando:', productData);

      const url = editingItem 
        ? `${API_URL}/api/admin/products/${editingItem.id}`
        : `${API_URL}/api/admin/products`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-Request-ID': requestId  // Para idempotência no backend
        },
        body: JSON.stringify(productData)
      });

      const result = await response.json();
      console.log('[SAVE PRODUCT] Resposta:', response.status, result);

      if (response.ok) {
        const isDuplicate = result.duplicate_request;
        setSaveMessage({ 
          type: 'success', 
          text: editingItem ? 'Produto atualizado com sucesso!' : (isDuplicate ? 'Produto já foi criado!' : 'Produto criado com sucesso!')
        });
        fetchAllData();
        setTimeout(() => setShowModal(false), 1500);
      } else {
        console.error('[SAVE PRODUCT] Erro:', result);
        setSaveMessage({ type: 'error', text: result.detail || result.message || 'Erro ao salvar produto' });
      }
    } catch (error) {
      console.error('[SAVE PRODUCT] Exception:', error);
      setSaveMessage({ type: 'error', text: 'Erro de conexão. Tente novamente.' });
    } finally {
      setSaveLoading(false);
      saveInProgressRef.current = false;
    }
  };


  const saveUpsell = async () => {
    setSaveLoading(true);
    try {
      const url = editingItem 
        ? `${API_URL}/api/admin/upsells/${editingItem.id}`
        : `${API_URL}/api/admin/upsells`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSaveMessage({ type: 'success', text: 'Salvo!' });
        fetchAllData();
        setTimeout(() => setShowModal(false), 1000);
      }
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Erro' });
    } finally {
      setSaveLoading(false);
    }
  };

  // ========== DELETE ITEM ==========
  
  // Abre modal de confirmação
  const openDeleteModal = (type, id, title = '') => {
    console.log(`[DELETE] Opening modal for ${type}: ${id} (${title})`);
    setDeleteModal({ show: true, type, id, title });
  };

  // Fecha modal
  const closeDeleteModal = () => {
    setDeleteModal({ show: false, type: '', id: '', title: '' });
  };

  // Executa exclusão
  const confirmDelete = async () => {
    const { type, id } = deleteModal;
    console.log(`[DELETE] Confirming delete for ${type}: ${id}`);
    
    setDeleting(true);
    
    try {
      const url = type === 'product' 
        ? `${API_URL}/api/admin/products/${id}`
        : `${API_URL}/api/admin/upsells/${id}`;
      
      console.log(`[DELETE] Sending DELETE request to: ${url}`);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const result = await response.json();
      console.log(`[DELETE] Response:`, response.status, result);
      
      if (response.ok) {
        setSaveMessage({ type: 'success', text: result.message || 'Excluído com sucesso!' });
        // Remove da lista local imediatamente
        if (type === 'product') {
          setProducts(prev => prev.filter(p => p.id !== id));
        } else {
          setUpsells(prev => prev.filter(u => u.id !== id));
        }
        closeDeleteModal();
      } else {
        // Erro do servidor - mostra mensagem
        const errorMsg = result.detail || result.message || 'Erro ao excluir';
        console.error(`[DELETE] Error:`, errorMsg);
        setSaveMessage({ type: 'error', text: errorMsg });
        
        // Se não pode excluir, oferece desativar
        if (response.status === 400 || response.status === 409) {
          if (type === 'product' && window.confirm(`Não foi possível excluir. Deseja DESATIVAR o produto em vez disso?`)) {
            await archiveProduct(id);
          }
        }
      }
    } catch (error) {
      console.error('[DELETE] Network error:', error);
      setSaveMessage({ type: 'error', text: 'Erro de conexão ao excluir' });
    } finally {
      setDeleting(false);
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 4000);
    }
  };

  // Arquiva/Desativa produto como alternativa à exclusão
  const archiveProduct = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ is_active: false })
      });
      
      if (response.ok) {
        setSaveMessage({ type: 'success', text: 'Produto desativado com sucesso' });
        fetchAllData();
        closeDeleteModal();
      }
    } catch (error) {
      console.error('Error archiving:', error);
    }
  };

  // Função antiga (mantida para compatibilidade, mas usando o novo modal)
  const deleteItem = (type, id, title = '') => {
    openDeleteModal(type, id, title);
  };

  const toggleFeatured = async (product) => {
    try {
      await fetch(`${API_URL}/api/admin/products/${product.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ 
          is_featured: !product.featured,
          featured_order: product.featured ? 99 : 0
        })
      });
      fetchAllData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // ========== DUPLICATE PRODUCT ==========
  
  const duplicateProduct = async (productId) => {
    setDuplicatingId(productId);
    try {
      const response = await fetch(`${API_URL}/api/admin/products/${productId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSaveMessage({ type: 'success', text: `Produto duplicado: ${data.title}` });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
        fetchAllData();
      } else {
        const err = await response.json();
        setSaveMessage({ type: 'error', text: err.detail || 'Erro ao duplicar' });
      }
    } catch (error) {
      console.error('Error duplicating:', error);
      setSaveMessage({ type: 'error', text: 'Erro de conexão' });
    } finally {
      setDuplicatingId(null);
    }
  };

  // ========== IMAGE UPLOAD ==========

  const handleImageUpload = async (e, field = 'images') => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadLoading(true);

    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(`${API_URL}/api/admin/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` },
          body: formDataUpload
        });

        if (response.ok) {
          const data = await response.json();
          // Supabase returns full URL
          uploadedUrls.push(data.url);
        }
      }

      if (field === 'images') {
        setFormData(prev => ({
          ...prev,
          images: [...(prev.images || []), ...uploadedUrls]
        }));
      } else {
        setFormData(prev => ({ ...prev, [field]: uploadedUrls[0] }));
      }

    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Upload para Hero/Banner
  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setHeroUploadLoading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image_url: data.url }));
      }
    } catch (error) {
      console.error('Hero upload error:', error);
    } finally {
      setHeroUploadLoading(false);
      if (heroFileInputRef.current) heroFileInputRef.current.value = '';
    }
  };

  // ========== FAVICON UPLOAD ==========
  
  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validação de formato
    const allowedTypes = ['image/png', 'image/x-icon', 'image/svg+xml', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      setSaveMessage({ type: 'error', text: 'Formato inválido. Use PNG, ICO, SVG ou JPG.' });
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      return;
    }

    setFaviconUploadLoading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFaviconUrl(data.url);
        setSaveMessage({ type: 'success', text: 'Favicon carregado! Clique em Salvar para aplicar.' });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Favicon upload error:', error);
      setSaveMessage({ type: 'error', text: 'Erro ao fazer upload do favicon.' });
    } finally {
      setFaviconUploadLoading(false);
      if (faviconFileInputRef.current) faviconFileInputRef.current.value = '';
    }
  };

  const saveFavicon = async () => {
    setSaveLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/favicon`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ url: faviconUrl })
      });

      if (response.ok) {
        setSaveMessage({ type: 'success', text: 'Favicon salvo! A página será atualizada.' });
        // Força atualização do favicon no navegador
        const existingFavicon = document.querySelector('link[rel="icon"]');
        if (existingFavicon) existingFavicon.remove();
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = `${faviconUrl}?v=${Date.now()}`;
        document.head.appendChild(link);
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      } else {
        const err = await response.json();
        setSaveMessage({ type: 'error', text: err.detail || 'Erro ao salvar favicon.' });
      }
    } catch (error) {
      console.error('Error saving favicon:', error);
      setSaveMessage({ type: 'error', text: 'Erro de conexão.' });
    } finally {
      setSaveLoading(false);
    }
  };

  const resetFavicon = () => {
    setFaviconUrl('');
    setSaveMessage({ type: 'info', text: 'Favicon resetado. Salve para aplicar.' });
    setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
  };

  // Upload para variante de cor específica - suporta múltiplas imagens (máx 3)
  const handleVariantImageUpload = async (e, variantIndex) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const variant = formData.color_variants?.[variantIndex];
    const currentImages = variant?.images || [];
    
    // Limite de 3 imagens por cor
    if (currentImages.length >= 3) {
      setSaveMessage({ type: 'error', text: 'Máximo de 3 imagens por cor' });
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      return;
    }

    setVariantUploadLoading(prev => ({ ...prev, [variantIndex]: true }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        const updated = [...(formData.color_variants || [])];
        const newImages = [...currentImages, data.url];
        updated[variantIndex] = { 
          ...updated[variantIndex], 
          images: newImages,
          image_url: newImages[0] // Mantém compatibilidade com image_url legado
        };
        setFormData(prev => ({ ...prev, color_variants: updated }));
      }
    } catch (error) {
      console.error('Variant upload error:', error);
    } finally {
      setVariantUploadLoading(prev => ({ ...prev, [variantIndex]: false }));
      if (variantFileInputRefs.current[variantIndex]) {
        variantFileInputRefs.current[variantIndex].value = '';
      }
    }
  };

  // Remove imagem específica de uma variante
  const removeVariantImage = (variantIndex, imageIndex) => {
    const updated = [...(formData.color_variants || [])];
    const variant = updated[variantIndex];
    const newImages = [...(variant.images || [])];
    newImages.splice(imageIndex, 1);
    updated[variantIndex] = { 
      ...variant, 
      images: newImages,
      image_url: newImages[0] || '' // Atualiza image_url legado
    };
    setFormData(prev => ({ ...prev, color_variants: updated }));
  };

  // Copiar URL para clipboard
  const copyToClipboard = async (url, identifier) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(identifier);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  // Extrair ID do vídeo do YouTube
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    // Padrões de URL do YouTube
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // ========== MODAL HANDLERS ==========

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setSaveMessage({ type: '', text: '' });

    switch (type) {
      case 'hero':
        setFormData(settings?.hero || {});
        break;
      case 'product':
        // Parse color_variants se vier como string JSON
        let colorVariants = [];
        if (item?.color_variants) {
          try {
            colorVariants = typeof item.color_variants === 'string' 
              ? JSON.parse(item.color_variants) 
              : item.color_variants;
          } catch (e) {
            colorVariants = [];
          }
        }
        // Migra image_url para images array se necessário
        colorVariants = colorVariants.map(v => {
          if (!v.images && v.image_url) {
            return { ...v, images: [v.image_url] };
          }
          return v;
        });
        // Parse storage_variants (novo sistema simplificado)
        let storageVariants = [];
        if (item?.storage_variants) {
          try {
            storageVariants = typeof item.storage_variants === 'string'
              ? JSON.parse(item.storage_variants)
              : item.storage_variants;
          } catch { storageVariants = []; }
        }
        setFormData(item ? {
          ...item,
          storage_variants: storageVariants,
          color_variants: colorVariants,
          price: item.price_pix || item.price || '',
          video_url: item.video_url || ''
        } : {
          brand: '', model: '', price: '', market_price: '',
          condition: 'new', description: '', images: [],
          stock: 1, featured: false, is_active: true, category: 'CELULARES',
          storage: '', storage_variants: [], color_variants: [], video_url: ''
        });
        break;
      case 'brand':
        setFormData(item || {
          key: '', label: '', description: '', icon: 'Smartphone',
          color: '#EAEAEA', filter_query: '', order: 0, is_active: true
        });
        break;
      case 'editable':
        setFormData(settings?.editable_section || { title: '', text: '', is_active: false });
        break;
      case 'footer':
        setFormData(settings?.footer || {});
        break;
      case 'upsell':
        setFormData(item || {
          title: '', description: '', price: '', image_url: '',
          applies_to: 'ALL', is_active: true
        });
        break;
      default:
        setFormData({});
    }
    setShowModal(true);
  };

  const handleSave = () => {
    switch (modalType) {
      case 'hero':
        saveSettings('hero', formData);
        break;
      case 'product':
        saveProduct();
        break;
      case 'brand':
        const updatedBrands = editingItem 
          ? settings.brands_section.items.map(b => b.key === editingItem.key ? formData : b)
          : [...(settings?.brands_section?.items || []), formData];
        saveSettings('brands', { ...settings?.brands_section, items: updatedBrands });
        break;
      case 'editable':
        saveSettings('editable-section', formData);
        break;
      case 'footer':
        saveSettings('footer', formData);
        break;
      case 'upsell':
        saveUpsell();
        break;
      default:
        break;
    }
  };

  // ========== HELPERS ==========

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchTerm || 
      p.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.model?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBrand = !filterBrand || p.brand === filterBrand;
    return matchesSearch && matchesBrand;
  });

  const featuredProducts = products.filter(p => p.featured).sort((a, b) => (a.featured_order || 99) - (b.featured_order || 99));
  const brands = [...new Set(products.map(p => p.brand))].sort();

  // ========== TABS CONFIG ==========

  const tabs = [
    { id: 'produtos', label: 'Produtos', icon: Package },
    { id: 'destaques', label: 'Destaques', icon: Star },
    { id: 'hero', label: 'Banner', icon: Layout },
    { id: 'marcas', label: 'Marcas', icon: Smartphone },
    { id: 'secao', label: 'Seção Texto', icon: Type },
    { id: 'footer', label: 'Footer', icon: Settings },
    { id: 'aparencia', label: 'Aparência', icon: Image },
    { id: 'upsell', label: 'Upsell', icon: Gift },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  // ========== LOGIN SCREEN ==========

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0F0F10] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#C9A24D]/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#C9A24D]/30">
                <Package className="h-10 w-10 text-[#C9A24D]" />
              </div>
              <h1 className="text-2xl font-bold text-[#EAEAEA]">Painel Admin</h1>
              <p className="text-[#666] text-sm mt-2">MULTICELL</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#B5B5B5] mb-2">Usuário</label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA] focus:ring-2 focus:ring-[#C9A24D] focus:border-transparent"
                  placeholder="Digite seu usuário"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B5B5B5] mb-2">Senha</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA] focus:ring-2 focus:ring-[#C9A24D] focus:border-transparent"
                  placeholder="Digite sua senha"
                  required
                />
              </div>

              {loginError && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <AlertCircle className="h-4 w-4" />
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] py-3 rounded-lg font-bold uppercase tracking-wider transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loginLoading ? <Loader className="h-5 w-5 animate-spin" /> : <><LogIn className="h-5 w-5" /> Entrar</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ========== ADMIN DASHBOARD ==========

  return (
    <div className="min-h-screen bg-[#0F0F10]">
      {/* Header */}
      <header className="bg-[#1C1C1F] border-b border-[#2A2A2D] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C9A24D]/10 rounded-full flex items-center justify-center border border-[#C9A24D]/30">
              <Package className="h-5 w-5 text-[#C9A24D]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#EAEAEA]">Admin MULTICELL</h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <a href="/" target="_blank" className="text-[#B5B5B5] hover:text-[#C9A24D] text-sm flex items-center gap-1">
              <Home className="h-4 w-4" /> Ver Site
            </a>
            <button onClick={handleLogout} className="flex items-center gap-2 text-[#B5B5B5] hover:text-red-400 transition-colors">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-[#1C1C1F] border-b border-[#2A2A2D] overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-[#C9A24D] border-b-2 border-[#C9A24D]'
                    : 'text-[#B5B5B5] hover:text-[#EAEAEA]'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        
        {/* TAB: PRODUTOS */}
        {activeTab === 'produtos' && (
          <div className="space-y-4">
            {/* Feedback Message */}
            {saveMessage.text && !showModal && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                saveMessage.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
                  : 'bg-red-500/10 border border-red-500/30 text-red-400'
              }`}>
                {saveMessage.type === 'success' ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <AlertCircle className="h-5 w-5" />
                )}
                <span>{saveMessage.text}</span>
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex gap-3 flex-1 w-full md:w-auto">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666]" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-[#1C1C1F] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]"
                  />
                </div>
                <select
                  value={filterBrand}
                  onChange={(e) => setFilterBrand(e.target.value)}
                  className="px-4 py-2.5 bg-[#1C1C1F] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]"
                >
                  <option value="">Todas Marcas</option>
                  {brands.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <button
                onClick={() => openModal('product')}
                className="flex items-center gap-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-4 py-2.5 rounded-lg font-bold"
              >
                <Plus className="h-5 w-5" /> Novo Produto
              </button>
            </div>

            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#2A2A2D] flex justify-between items-center">
                <h2 className="font-bold text-[#EAEAEA]">Produtos ({filteredProducts.length})</h2>
              </div>

              <div className="divide-y divide-[#2A2A2D]">
                {filteredProducts.map(product => (
                  <div key={product.id} className="p-4 flex items-center gap-4 hover:bg-[#2A2A2D]/30">
                    <div className="w-16 h-16 bg-[#0F0F10] rounded-lg overflow-hidden flex-shrink-0 border border-[#2A2A2D]">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt="" className="w-full h-full object-contain p-1" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Image className="h-6 w-6 text-[#666]" /></div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#EAEAEA] truncate">{product.brand} {product.model}</h3>
                        {product.featured && <Star className="h-4 w-4 text-[#C9A24D] fill-current flex-shrink-0" />}
                        {!product.is_active && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Inativo</span>}
                      </div>
                      <p className="text-[#C9A24D] font-bold">{formatPrice(product.price)}</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleFeatured(product)} className={`p-2 rounded-lg ${product.featured ? 'bg-[#C9A24D]/20 text-[#C9A24D]' : 'bg-[#2A2A2D] text-[#666]'}`} title="Destacar">
                        <Star className={`h-5 w-5 ${product.featured ? 'fill-current' : ''}`} />
                      </button>
                      <button 
                        onClick={() => duplicateProduct(product.id)} 
                        disabled={duplicatingId === product.id}
                        className="p-2 bg-[#2A2A2D] text-blue-400 rounded-lg hover:bg-blue-500/20 disabled:opacity-50" 
                        title="Duplicar"
                        data-testid={`duplicate-product-${product.id}`}
                      >
                        {duplicatingId === product.id ? (
                          <Loader className="h-5 w-5 animate-spin" />
                        ) : (
                          <Files className="h-5 w-5" />
                        )}
                      </button>
                      <button onClick={() => openModal('product', product)} className="p-2 bg-[#2A2A2D] text-[#EAEAEA] rounded-lg hover:bg-[#3A3A3D]" title="Editar">
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => deleteItem('product', product.id, product.title)}
                        className="p-2 bg-[#2A2A2D] text-red-400 rounded-lg hover:bg-red-500/20" 
                        title="Excluir"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB: DESTAQUES */}
        {activeTab === 'destaques' && (
          <div className="space-y-4">
            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
              <h2 className="text-xl font-bold text-[#EAEAEA] mb-2">Produtos em Destaque</h2>
              <p className="text-[#B5B5B5] text-sm mb-6">
                Selecione até 4 produtos para aparecer na homepage. Use a estrela na aba "Produtos" para adicionar/remover.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[0, 1, 2, 3].map(i => {
                  const product = featuredProducts[i];
                  return (
                    <div key={i} className={`border-2 border-dashed rounded-xl p-4 ${product ? 'border-[#C9A24D] bg-[#C9A24D]/5' : 'border-[#2A2A2D]'}`}>
                      {product ? (
                        <div className="text-center">
                          <div className="w-20 h-20 mx-auto bg-[#0F0F10] rounded-lg mb-3 overflow-hidden">
                            {product.images?.[0] && <img src={product.images[0]} alt="" className="w-full h-full object-contain p-2" />}
                          </div>
                          <p className="text-sm font-semibold text-[#EAEAEA] truncate">{product.brand} {product.model}</p>
                          <p className="text-[#C9A24D] font-bold text-sm">{formatPrice(product.price)}</p>
                          <span className="inline-block mt-2 text-xs bg-[#C9A24D]/20 text-[#C9A24D] px-2 py-1 rounded">Posição {i + 1}</span>
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <ShoppingBag className="h-10 w-10 text-[#666] mx-auto mb-2" />
                          <p className="text-[#666] text-sm">Vazio</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="text-[#666] text-xs mt-4 text-center">
                Dica: Na aba "Produtos", clique na estrela ⭐ para destacar/remover produtos.
              </p>
            </div>
          </div>
        )}

        {/* TAB: HERO/BANNER */}
        {activeTab === 'hero' && (
          <div className="space-y-4">
            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[#EAEAEA]">Banner Principal</h2>
                <button
                  onClick={() => openModal('hero')}
                  className="flex items-center gap-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-4 py-2 rounded-lg font-bold"
                >
                  <Edit2 className="h-4 w-4" /> Editar
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-[#666] text-xs uppercase mb-1">Badge</p>
                  <p className="text-[#C9A24D] font-semibold">{settings?.hero?.badge || '-'}</p>
                  
                  <p className="text-[#666] text-xs uppercase mt-4 mb-1">Título</p>
                  <p className="text-[#EAEAEA] text-xl font-bold">{settings?.hero?.title || '-'}</p>
                  
                  <p className="text-[#666] text-xs uppercase mt-4 mb-1">Subtítulo</p>
                  <p className="text-[#B5B5B5]">{settings?.hero?.subtitle || '-'}</p>
                  
                  <p className="text-[#666] text-xs uppercase mt-4 mb-1">Botão CTA</p>
                  <p className="text-[#EAEAEA]">{settings?.hero?.cta_text || '-'} → {settings?.hero?.cta_link || '-'}</p>
                </div>
                
                <div className="bg-[#0F0F10] rounded-xl p-4 border border-[#2A2A2D]">
                  <p className="text-[#666] text-xs uppercase mb-2">Imagem</p>
                  {settings?.hero?.image_url ? (
                    <img src={settings.hero.image_url} alt="Hero" className="w-full h-48 object-contain" />
                  ) : (
                    <div className="h-48 flex items-center justify-center text-[#666]">Sem imagem</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB: MARCAS */}
        {activeTab === 'marcas' && (
          <div className="space-y-4">
            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-[#EAEAEA]">Marcas Consolidadas</h2>
                  <p className="text-[#B5B5B5] text-sm">{settings?.brands_section?.subtitle}</p>
                </div>
                <button
                  onClick={() => openModal('brand')}
                  className="flex items-center gap-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-4 py-2 rounded-lg font-bold"
                >
                  <Plus className="h-4 w-4" /> Nova Marca
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {(settings?.brands_section?.items || []).map(brand => (
                  <div key={brand.key} className={`bg-[#0F0F10] border rounded-xl p-4 ${brand.is_active ? 'border-[#2A2A2D]' : 'border-red-500/30 opacity-50'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="w-10 h-10 bg-[#1C1C1F] rounded-full flex items-center justify-center" style={{ borderColor: brand.color }}>
                        <Smartphone className="h-5 w-5" style={{ color: brand.color }} />
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openModal('brand', brand)} className="p-1.5 text-[#B5B5B5] hover:text-[#C9A24D]">
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <h3 className="font-bold text-[#EAEAEA]">{brand.label}</h3>
                    <p className="text-[#666] text-sm">{brand.description}</p>
                    <p className="text-[#C9A24D] text-xs mt-2">Ordem: {brand.order}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB: SEÇÃO TEXTO */}
        {activeTab === 'secao' && (
          <div className="space-y-4">
            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[#EAEAEA]">Seção de Texto Editável</h2>
                <button
                  onClick={() => openModal('editable')}
                  className="flex items-center gap-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-4 py-2 rounded-lg font-bold"
                >
                  <Edit2 className="h-4 w-4" /> Editar
                </button>
              </div>

              <div className={`p-4 rounded-lg border ${settings?.editable_section?.is_active ? 'border-green-500/30 bg-green-500/5' : 'border-[#2A2A2D]'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${settings?.editable_section?.is_active ? 'bg-green-500/20 text-green-400' : 'bg-[#2A2A2D] text-[#666]'}`}>
                    {settings?.editable_section?.is_active ? 'ATIVO' : 'INATIVO'}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-[#EAEAEA] mb-2">{settings?.editable_section?.title || 'Sem título'}</h3>
                <p className="text-[#B5B5B5]">{settings?.editable_section?.text || 'Sem texto definido'}</p>
              </div>

              <p className="text-[#666] text-sm mt-4">Esta seção aparece logo após as "Marcas Consolidadas" na homepage quando ativa.</p>
            </div>
          </div>
        )}

        {/* TAB: FOOTER */}
        {activeTab === 'footer' && (
          <div className="space-y-4">
            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[#EAEAEA]">Configurações do Footer</h2>
                <button
                  onClick={() => openModal('footer')}
                  className="flex items-center gap-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-4 py-2 rounded-lg font-bold"
                >
                  <Edit2 className="h-4 w-4" /> Editar
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-[#666] text-xs uppercase mb-1 flex items-center gap-1"><Type className="h-3 w-3" /> Texto Sobre</p>
                  <p className="text-[#B5B5B5]">{settings?.footer?.about_text || '-'}</p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-[#666] text-xs uppercase mb-1 flex items-center gap-1"><Phone className="h-3 w-3" /> Telefone</p>
                    <p className="text-[#EAEAEA]">{settings?.footer?.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[#666] text-xs uppercase mb-1 flex items-center gap-1"><Mail className="h-3 w-3" /> Email</p>
                    <p className="text-[#EAEAEA]">{settings?.footer?.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[#666] text-xs uppercase mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Localização</p>
                    <p className="text-[#EAEAEA]">{settings?.footer?.location || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[#666] text-xs uppercase mb-1 flex items-center gap-1"><Instagram className="h-3 w-3" /> Instagram</p>
                    <p className="text-[#EAEAEA]">{settings?.footer?.instagram || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB: APARÊNCIA (FAVICON) */}
        {activeTab === 'aparencia' && (
          <div className="space-y-6">
            {/* Feedback Message */}
            {saveMessage.text && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                saveMessage.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
                  : saveMessage.type === 'error'
                  ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                  : 'bg-blue-500/10 border border-blue-500/30 text-blue-400'
              }`}>
                {saveMessage.type === 'success' ? (
                  <CheckCircle className="h-5 w-5" />
                ) : saveMessage.type === 'error' ? (
                  <AlertCircle className="h-5 w-5" />
                ) : (
                  <AlertCircle className="h-5 w-5" />
                )}
                <span>{saveMessage.text}</span>
              </div>
            )}

            {/* Favicon Section */}
            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-[#EAEAEA] flex items-center gap-2">
                  <Image className="h-5 w-5 text-[#C9A24D]" />
                  Favicon do Site
                </h2>
                <p className="text-[#B5B5B5] text-sm mt-1">O favicon é o ícone que aparece na aba do navegador</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-[#B5B5B5] mb-3">Preview Atual</label>
                  <div className="bg-[#0F0F10] border border-[#2A2A2D] rounded-xl p-6 flex flex-col items-center">
                    {faviconUrl ? (
                      <>
                        <div className="w-24 h-24 bg-[#1C1C1F] rounded-lg flex items-center justify-center border border-[#2A2A2D] mb-4">
                          <img 
                            src={faviconUrl} 
                            alt="Favicon" 
                            className="max-w-16 max-h-16 object-contain"
                          />
                        </div>
                        <p className="text-[#666] text-xs text-center mb-2">Como aparece no navegador:</p>
                        <div className="bg-[#2A2A2D] rounded-t-lg px-4 py-2 flex items-center gap-2">
                          <img src={faviconUrl} alt="" className="w-4 h-4" />
                          <span className="text-xs text-[#EAEAEA]">MULTICELL</span>
                          <span className="text-[#666] ml-2">×</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <div className="w-24 h-24 bg-[#1C1C1F] rounded-lg flex items-center justify-center border border-dashed border-[#2A2A2D] mb-4">
                          <Image className="h-8 w-8 text-[#666]" />
                        </div>
                        <p className="text-[#666] text-sm">Nenhum favicon personalizado</p>
                        <p className="text-[#4A4A4A] text-xs">Será usado o padrão do navegador</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload */}
                <div>
                  <label className="block text-sm font-medium text-[#B5B5B5] mb-3">Upload de Favicon</label>
                  
                  <input 
                    type="file" 
                    ref={faviconFileInputRef}
                    onChange={handleFaviconUpload}
                    accept=".png,.ico,.svg,.jpg,.jpeg"
                    className="hidden"
                    id="favicon-upload"
                  />
                  
                  <label 
                    htmlFor="favicon-upload"
                    className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-[#2A2A2D] rounded-xl cursor-pointer hover:border-[#C9A24D] transition-colors ${faviconUploadLoading ? 'opacity-50' : ''}`}
                  >
                    {faviconUploadLoading ? (
                      <Loader className="h-8 w-8 text-[#C9A24D] animate-spin mb-2" />
                    ) : (
                      <Upload className="h-8 w-8 text-[#C9A24D] mb-2" />
                    )}
                    <span className="text-[#B5B5B5] font-medium">
                      {faviconUploadLoading ? 'Enviando...' : 'Clique para fazer upload'}
                    </span>
                    <span className="text-[#666] text-xs mt-1">PNG, ICO, SVG ou JPG (recomendado: 32x32 ou 64x64)</span>
                  </label>

                  {/* URL Input */}
                  <div className="mt-4">
                    <label className="block text-sm text-[#666] mb-1">Ou cole a URL da imagem:</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={faviconUrl}
                        onChange={(e) => setFaviconUrl(e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA] text-sm"
                        placeholder="https://..."
                      />
                      {faviconUrl && (
                        <button
                          onClick={() => copyToClipboard(faviconUrl, 'favicon')}
                          className="px-3 py-2 bg-[#2A2A2D] hover:bg-[#3A3A3D] rounded-lg transition-colors"
                          title="Copiar URL"
                        >
                          {copiedUrl === 'favicon' ? (
                            <Check className="h-4 w-4 text-green-400" />
                          ) : (
                            <Copy className="h-4 w-4 text-[#B5B5B5]" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={saveFavicon}
                      disabled={saveLoading}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-4 py-3 rounded-lg font-bold disabled:opacity-50"
                    >
                      {saveLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Salvar Favicon
                    </button>
                    <button
                      onClick={resetFavicon}
                      className="px-4 py-3 bg-[#2A2A2D] hover:bg-[#3A3A3D] text-[#EAEAEA] rounded-lg font-medium flex items-center gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Restaurar Padrão
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB: UPSELL */}
        {activeTab === 'upsell' && (
          <div className="space-y-4">
            <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-[#EAEAEA]">Upsells / Cross-sell</h2>
                  <p className="text-[#B5B5B5] text-sm">Ofertas adicionais no checkout (ex: Kit Capinha + Película)</p>
                </div>
                <button
                  onClick={() => openModal('upsell')}
                  className="flex items-center gap-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-4 py-2 rounded-lg font-bold"
                >
                  <Plus className="h-4 w-4" /> Novo Upsell
                </button>
              </div>

              {upsells.length === 0 ? (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-[#666] mx-auto mb-3" />
                  <p className="text-[#666]">Nenhum upsell cadastrado</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upsells.map(upsell => (
                    <div key={upsell.id} className={`bg-[#0F0F10] border rounded-xl p-4 flex items-center gap-4 ${upsell.is_active ? 'border-[#2A2A2D]' : 'border-red-500/30 opacity-50'}`}>
                      <div className="w-16 h-16 bg-[#1C1C1F] rounded-lg overflow-hidden flex-shrink-0">
                        {upsell.image_url ? (
                          <img src={upsell.image_url} alt="" className="w-full h-full object-contain p-1" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Gift className="h-6 w-6 text-[#666]" /></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-[#EAEAEA]">{upsell.title}</h3>
                        <p className="text-[#666] text-sm">{upsell.description}</p>
                        <p className="text-[#C9A24D] font-bold">{formatPrice(upsell.price)}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openModal('upsell', upsell)} className="p-2 bg-[#2A2A2D] text-[#EAEAEA] rounded-lg">
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => deleteItem('upsell', upsell.id, upsell.title)} className="p-2 bg-[#2A2A2D] text-red-400 rounded-lg">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: ANALYTICS */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#EAEAEA]">Dashboard de Analytics</h2>
              <div className="flex items-center gap-3">
                <select
                  value={analyticsPeriod}
                  onChange={(e) => {
                    const days = parseInt(e.target.value);
                    setAnalyticsPeriod(days);
                    fetchAnalytics(days);
                  }}
                  className="px-3 py-2 bg-[#2A2A2D] border border-[#3A3A3D] rounded-lg text-[#EAEAEA]"
                >
                  <option value={7}>Últimos 7 dias</option>
                  <option value={14}>Últimos 14 dias</option>
                  <option value={30}>Últimos 30 dias</option>
                  <option value={60}>Últimos 60 dias</option>
                  <option value={90}>Últimos 90 dias</option>
                </select>
                <button
                  onClick={() => fetchAnalytics(analyticsPeriod)}
                  disabled={analyticsLoading}
                  className="p-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] rounded-lg"
                >
                  <RefreshCw className={`h-5 w-5 ${analyticsLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {analyticsLoading && !analytics ? (
              <div className="flex items-center justify-center py-20">
                <Loader className="h-8 w-8 text-[#C9A24D] animate-spin" />
              </div>
            ) : analytics ? (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#C9A24D]/10 rounded-lg flex items-center justify-center">
                        <Eye className="h-5 w-5 text-[#C9A24D]" />
                      </div>
                      <span className="text-[#666] text-sm">Hoje</span>
                    </div>
                    <p className="text-2xl font-bold text-[#EAEAEA]">{analytics.views_today}</p>
                    <p className="text-[#666] text-xs">visualizações</p>
                  </div>

                  <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-blue-400" />
                      </div>
                      <span className="text-[#666] text-sm">Semana</span>
                    </div>
                    <p className="text-2xl font-bold text-[#EAEAEA]">{analytics.views_this_week}</p>
                    <p className="text-[#666] text-xs">visualizações</p>
                  </div>

                  <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-green-400" />
                      </div>
                      <span className="text-[#666] text-sm">Mês</span>
                    </div>
                    <p className="text-2xl font-bold text-[#EAEAEA]">{analytics.views_this_month}</p>
                    <p className="text-[#666] text-xs">visualizações</p>
                  </div>

                  <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                        <Users className="h-5 w-5 text-purple-400" />
                      </div>
                      <span className="text-[#666] text-sm">Únicos</span>
                    </div>
                    <p className="text-2xl font-bold text-[#EAEAEA]">{analytics.unique_visitors}</p>
                    <p className="text-[#666] text-xs">visitantes</p>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Views by Day Chart */}
                  <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                    <h3 className="text-[#EAEAEA] font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-[#C9A24D]" />
                      Visualizações por Dia
                    </h3>
                    <div className="h-48 flex items-end gap-1">
                      {analytics.views_by_day.slice(-14).map((day, i) => {
                        const maxViews = Math.max(...analytics.views_by_day.map(d => d.views), 1);
                        const height = (day.views / maxViews) * 100;
                        return (
                          <div
                            key={day.date}
                            className="flex-1 group relative"
                          >
                            <div
                              className="w-full bg-[#C9A24D] rounded-t transition-all hover:bg-[#E0B85C]"
                              style={{ height: `${Math.max(height, 2)}%` }}
                            />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-[#0F0F10] border border-[#2A2A2D] px-2 py-1 rounded text-xs whitespace-nowrap z-10">
                              <p className="text-[#EAEAEA] font-semibold">{day.views} views</p>
                              <p className="text-[#666]">{new Date(day.date).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric' })}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Device Breakdown */}
                  <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                    <h3 className="text-[#EAEAEA] font-semibold mb-4 flex items-center gap-2">
                      <Monitor className="h-4 w-4 text-[#C9A24D]" />
                      Dispositivos
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(analytics.device_breakdown).map(([device, count]) => {
                        const total = Object.values(analytics.device_breakdown).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={device} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-[#B5B5B5] capitalize flex items-center gap-2">
                                {device === 'mobile' && <MobileIcon className="h-4 w-4" />}
                                {device === 'desktop' && <Monitor className="h-4 w-4" />}
                                {device === 'tablet' && <Layout className="h-4 w-4" />}
                                {device}
                              </span>
                              <span className="text-[#EAEAEA] font-semibold">{percentage}%</span>
                            </div>
                            <div className="h-2 bg-[#2A2A2D] rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#C9A24D] rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Top Products */}
                <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                  <h3 className="text-[#EAEAEA] font-semibold mb-4 flex items-center gap-2">
                    <Star className="h-4 w-4 text-[#C9A24D]" />
                    Produtos Mais Vistos
                  </h3>
                  {analytics.top_products.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.top_products.map((product, index) => (
                        <div key={product.id} className="flex items-center gap-4 p-3 bg-[#0F0F10] rounded-lg">
                          <span className="text-[#666] font-bold w-6">{index + 1}º</span>
                          <div className="w-12 h-12 bg-[#1C1C1F] rounded-lg overflow-hidden flex-shrink-0">
                            {product.image_url ? (
                              <img src={product.image_url} alt="" className="w-full h-full object-contain p-1" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="h-5 w-5 text-[#666]" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#EAEAEA] font-medium truncate">{product.title}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[#C9A24D] font-bold">{product.views}</p>
                            <p className="text-[#666] text-xs">views</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Eye className="h-12 w-12 text-[#666] mx-auto mb-3" />
                      <p className="text-[#666]">Nenhuma visualização de produto ainda</p>
                      <p className="text-[#666] text-sm">As estatísticas aparecerão quando visitantes acessarem os produtos</p>
                    </div>
                  )}
                </div>

                {/* Page Breakdown */}
                <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                  <h3 className="text-[#EAEAEA] font-semibold mb-4 flex items-center gap-2">
                    <Layout className="h-4 w-4 text-[#C9A24D]" />
                    Visualizações por Página
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(analytics.page_breakdown).map(([page, count]) => (
                      <div key={page} className="bg-[#0F0F10] rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-[#EAEAEA]">{count}</p>
                        <p className="text-[#666] text-sm capitalize">{page.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <BarChart3 className="h-16 w-16 text-[#666] mx-auto mb-4" />
                <p className="text-[#666]">Nenhum dado de analytics disponível</p>
                <button
                  onClick={() => fetchAnalytics(analyticsPeriod)}
                  className="mt-4 px-4 py-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] rounded-lg font-bold"
                >
                  Carregar Analytics
                </button>
              </div>
            )}
          </div>
        )}

      </main>

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#EAEAEA]">Confirmar Exclusão</h3>
                <p className="text-[#666] text-sm">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            
            <div className="bg-[#0F0F10] border border-[#2A2A2D] rounded-lg p-4 mb-6">
              <p className="text-[#B5B5B5] text-sm">
                Tem certeza que deseja excluir{' '}
                <span className="text-[#EAEAEA] font-medium">
                  {deleteModal.title || (deleteModal.type === 'product' ? 'este produto' : 'este item')}
                </span>?
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-[#2A2A2D] hover:bg-[#3A3A3D] text-[#EAEAEA] rounded-lg font-medium disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Excluir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-4 border-b border-[#2A2A2D]">
              <h2 className="text-xl font-bold text-[#EAEAEA]">
                {modalType === 'hero' && 'Editar Banner'}
                {modalType === 'product' && (editingItem ? 'Editar Produto' : 'Novo Produto')}
                {modalType === 'brand' && (editingItem ? 'Editar Marca' : 'Nova Marca')}
                {modalType === 'editable' && 'Editar Seção'}
                {modalType === 'footer' && 'Editar Footer'}
                {modalType === 'upsell' && (editingItem ? 'Editar Upsell' : 'Novo Upsell')}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-[#666] hover:text-[#EAEAEA]">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 max-h-[80vh] overflow-y-auto space-y-4">
              
              {/* HERO FORM */}
              {modalType === 'hero' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Badge</label>
                    <input type="text" value={formData.badge || ''} onChange={(e) => setFormData({...formData, badge: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Título</label>
                    <input type="text" value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Subtítulo</label>
                    <textarea value={formData.subtitle || ''} onChange={(e) => setFormData({...formData, subtitle: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" rows="2" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Texto do Botão</label>
                      <input type="text" value={formData.cta_text || ''} onChange={(e) => setFormData({...formData, cta_text: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Link do Botão</label>
                      <input type="text" value={formData.cta_link || ''} onChange={(e) => setFormData({...formData, cta_link: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-2">Imagem do Banner</label>
                    
                    {/* Upload Button */}
                    <input 
                      type="file" 
                      ref={heroFileInputRef} 
                      onChange={handleHeroImageUpload} 
                      accept=".jpg,.jpeg,.png,.webp" 
                      className="hidden" 
                      id="hero-image-upload" 
                    />
                    <label 
                      htmlFor="hero-image-upload" 
                      className={`flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-[#2A2A2D] rounded-xl cursor-pointer hover:border-[#C9A24D] mb-3 ${heroUploadLoading ? 'opacity-50' : ''}`}
                    >
                      {heroUploadLoading ? (
                        <Loader className="h-5 w-5 text-[#C9A24D] animate-spin" />
                      ) : (
                        <Upload className="h-5 w-5 text-[#C9A24D]" />
                      )}
                      <span className="text-[#B5B5B5] text-sm">
                        {heroUploadLoading ? 'Enviando...' : 'Upload de imagem'}
                      </span>
                    </label>

                    {/* URL Input */}
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={formData.image_url || ''} 
                        onChange={(e) => setFormData({...formData, image_url: e.target.value})} 
                        className="flex-1 px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA] text-sm" 
                        placeholder="https://... ou faça upload acima" 
                      />
                      {formData.image_url && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(formData.image_url, 'hero')}
                          className="px-3 py-2 bg-[#2A2A2D] hover:bg-[#3A3A3D] rounded-lg transition-colors"
                          title="Copiar URL"
                        >
                          {copiedUrl === 'hero' ? (
                            <Check className="h-4 w-4 text-green-400" />
                          ) : (
                            <Copy className="h-4 w-4 text-[#B5B5B5]" />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Preview */}
                    {formData.image_url && (
                      <div className="mt-3 relative">
                        <img src={formData.image_url} alt="Preview" className="h-32 object-contain rounded-lg bg-[#0F0F10] p-2" />
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, image_url: ''})}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* PRODUCT FORM */}
              {modalType === 'product' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Marca *</label>
                      <select value={formData.brand || ''} onChange={(e) => setFormData({...formData, brand: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]">
                        <option value="">Selecione</option>
                        <option value="Apple">Apple</option>
                        <option value="Samsung">Samsung</option>
                        <option value="Xiaomi">Xiaomi</option>
                        <option value="Motorola">Motorola</option>
                        <option value="Outros">Outros</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Modelo *</label>
                      <input type="text" value={formData.model || ''} onChange={(e) => setFormData({...formData, model: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Preço *</label>
                      <input type="number" value={formData.price || ''} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Preço Original</label>
                      <input type="number" value={formData.market_price || ''} onChange={(e) => setFormData({...formData, market_price: parseFloat(e.target.value)})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Armazenamento</label>
                      <input type="text" value={formData.storage || ''} onChange={(e) => setFormData({...formData, storage: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="Ex: 256GB, 512GB" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Condição</label>
                      <select value={formData.condition || 'new'} onChange={(e) => setFormData({...formData, condition: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]">
                        <option value="new">Novo</option>
                        <option value="used">Seminovo</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Categoria</label>
                      <select value={formData.category || 'CELULARES'} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]">
                        <option value="CELULARES">Celulares</option>
                        <option value="SEMINOVOS">Seminovos</option>
                        <option value="ACESSORIOS">Acessórios</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Estoque</label>
                      <input type="number" value={formData.stock || 0} onChange={(e) => setFormData({...formData, stock: parseInt(e.target.value)})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Descrição</label>
                    <textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" rows="3" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-2">Imagens</label>
                    <input type="file" ref={fileInputRef} onChange={(e) => handleImageUpload(e, 'images')} accept=".jpg,.jpeg,.png,.webp" multiple className="hidden" id="product-images" />
                    <label htmlFor="product-images" className={`flex items-center justify-center gap-2 w-full py-4 border-2 border-dashed border-[#2A2A2D] rounded-xl cursor-pointer hover:border-[#C9A24D] ${uploadLoading ? 'opacity-50' : ''}`}>
                      {uploadLoading ? <Loader className="h-6 w-6 text-[#C9A24D] animate-spin" /> : <Upload className="h-6 w-6 text-[#C9A24D]" />}
                      <span className="text-[#B5B5B5]">{uploadLoading ? 'Enviando...' : 'Upload de imagens'}</span>
                    </label>
                    {formData.images?.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {formData.images.map((img, i) => (
                          <div key={i} className="bg-[#0F0F10] rounded-lg p-2">
                            <div className="flex items-center gap-3">
                              <img src={img} alt="" className="w-16 h-16 object-contain rounded-lg bg-[#1C1C1F] p-1 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <input 
                                    type="text" 
                                    value={img} 
                                    readOnly 
                                    className="flex-1 px-2 py-1.5 bg-[#1C1C1F] border border-[#2A2A2D] rounded text-[#B5B5B5] text-xs truncate"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(img, `img-${i}`)}
                                    className="p-1.5 bg-[#2A2A2D] hover:bg-[#3A3A3D] rounded transition-colors flex-shrink-0"
                                    title="Copiar URL"
                                  >
                                    {copiedUrl === `img-${i}` ? (
                                      <Check className="h-3.5 w-3.5 text-green-400" />
                                    ) : (
                                      <Copy className="h-3.5 w-3.5 text-[#B5B5B5]" />
                                    )}
                                  </button>
                                  <button 
                                    type="button"
                                    onClick={() => setFormData({...formData, images: formData.images.filter((_, idx) => idx !== i)})} 
                                    className="p-1.5 bg-[#2A2A2D] hover:bg-red-500/20 rounded transition-colors flex-shrink-0"
                                    title="Remover"
                                  >
                                    <X className="h-3.5 w-3.5 text-red-400" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* VIDEO SECTION */}
                  <div className="border border-[#2A2A2D] rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      <div>
                        <h4 className="text-[#EAEAEA] font-semibold">Vídeo do Produto</h4>
                        <p className="text-[#666] text-xs">Cole o link do YouTube para mostrar um vídeo do produto</p>
                      </div>
                    </div>
                    
                    <div>
                      <input 
                        type="text" 
                        value={formData.video_url || ''} 
                        onChange={(e) => setFormData({...formData, video_url: e.target.value})} 
                        className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA] text-sm" 
                        placeholder="https://www.youtube.com/watch?v=XXXXX ou https://youtu.be/XXXXX"
                      />
                    </div>

                    {/* Preview do vídeo */}
                    {formData.video_url && getYouTubeVideoId(formData.video_url) && (
                      <div className="relative">
                        <div className="aspect-video bg-[#0F0F10] rounded-lg overflow-hidden">
                          <iframe
                            src={`https://www.youtube.com/embed/${getYouTubeVideoId(formData.video_url)}`}
                            title="Preview do vídeo"
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, video_url: ''})}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}

                    {formData.video_url && !getYouTubeVideoId(formData.video_url) && (
                      <p className="text-red-400 text-xs flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Link inválido. Use um link do YouTube válido.
                      </p>
                    )}
                  </div>

                  {/* VARIAÇÕES DO PRODUTO - Sistema Simplificado */}
                  <div className="border-2 border-[#C9A24D]/30 rounded-xl p-4 bg-[#1C1C1F]/50">
                    <ProductVariantsEditor
                      storageVariants={formData.storage_variants || []}
                      colorVariants={formData.color_variants || []}
                      onStorageChange={(variants) => setFormData({ ...formData, storage_variants: variants })}
                      onColorChange={(variants) => setFormData({ ...formData, color_variants: variants })}
                      authToken={authToken}
                      enableColorPriceAdjust={formData.enable_color_price_adjust || false}
                      onToggleColorPriceAdjust={(enabled) => setFormData({ ...formData, enable_color_price_adjust: enabled })}
                      basePrice={parseFloat(formData.price) || parseFloat(formData.price_pix) || 0}
                    />
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-[#0F0F10] rounded-lg">
                    <input type="checkbox" id="is_active" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-5 h-5" />
                    <label htmlFor="is_active" className="text-[#EAEAEA]">Produto ativo (visível no site)</label>
                  </div>
                </>
              )}

              {/* BRAND FORM */}
              {modalType === 'brand' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Chave (único)</label>
                      <input type="text" value={formData.key || ''} onChange={(e) => setFormData({...formData, key: e.target.value.toLowerCase().replace(/\s/g, '_')})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="apple, samsung..." disabled={!!editingItem} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Nome (Label)</label>
                      <input type="text" value={formData.label || ''} onChange={(e) => setFormData({...formData, label: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Descrição</label>
                    <input type="text" value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Ícone</label>
                      <select value={formData.icon || 'Smartphone'} onChange={(e) => setFormData({...formData, icon: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]">
                        <option value="Smartphone">Smartphone</option>
                        <option value="RefreshCw">Refresh (Seminovos)</option>
                        <option value="Sparkles">Sparkles (Acessórios)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Cor do Ícone</label>
                      <input type="color" value={formData.color || '#EAEAEA'} onChange={(e) => setFormData({...formData, color: e.target.value})} className="w-full h-10 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg cursor-pointer" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Ordem</label>
                      <input type="number" value={formData.order || 0} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Filtro (query string)</label>
                    <input type="text" value={formData.filter_query || ''} onChange={(e) => setFormData({...formData, filter_query: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="brand=Apple ou condition=used" />
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#0F0F10] rounded-lg">
                    <input type="checkbox" id="brand_active" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-5 h-5" />
                    <label htmlFor="brand_active" className="text-[#EAEAEA]">Marca ativa (visível no site)</label>
                  </div>
                </>
              )}

              {/* EDITABLE SECTION FORM */}
              {modalType === 'editable' && (
                <>
                  <div className="flex items-center gap-3 p-3 bg-[#0F0F10] rounded-lg mb-4">
                    <input type="checkbox" id="section_active" checked={formData.is_active || false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-5 h-5" />
                    <label htmlFor="section_active" className="text-[#EAEAEA]">Seção ativa (mostrar no site)</label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Título</label>
                    <input type="text" value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Texto</label>
                    <textarea value={formData.text || ''} onChange={(e) => setFormData({...formData, text: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" rows="5" />
                  </div>
                </>
              )}

              {/* FOOTER FORM */}
              {modalType === 'footer' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Texto Sobre</label>
                    <textarea value={formData.about_text || ''} onChange={(e) => setFormData({...formData, about_text: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" rows="3" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Telefone</label>
                      <input type="text" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">WhatsApp (só números)</label>
                      <input type="text" value={formData.whatsapp || ''} onChange={(e) => setFormData({...formData, whatsapp: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="5543999999999" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Email</label>
                      <input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Localização</label>
                      <input type="text" value={formData.location || ''} onChange={(e) => setFormData({...formData, location: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Instagram (URL)</label>
                      <input type="text" value={formData.instagram || ''} onChange={(e) => setFormData({...formData, instagram: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="https://instagram.com/..." />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Facebook (URL)</label>
                      <input type="text" value={formData.facebook || ''} onChange={(e) => setFormData({...formData, facebook: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="https://facebook.com/..." />
                    </div>
                  </div>
                </>
              )}

              {/* UPSELL FORM */}
              {modalType === 'upsell' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Título *</label>
                    <input type="text" value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="Kit Capinha + Película" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Descrição</label>
                    <textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" rows="2" placeholder="Proteção completa para seu smartphone" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Preço *</label>
                      <input type="number" step="0.01" value={formData.price || ''} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="79.90" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Ordem</label>
                      <input type="number" value={formData.sort_order || 0} onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})} className="w-full px-3 py-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B5B5B5] mb-1">Imagem</label>
                    <div className="flex gap-3">
                      {formData.image_url && (
                        <div className="w-20 h-20 bg-[#0F0F10] rounded-lg overflow-hidden flex-shrink-0 border border-[#2A2A2D]">
                          <img src={formData.image_url} alt="" className="w-full h-full object-contain p-1" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={(e) => handleImageUpload(e, 'image_url')}
                          className="hidden"
                          ref={fileInputRef}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadLoading}
                          className="w-full px-3 py-2 bg-[#2A2A2D] hover:bg-[#3A3A3D] text-[#EAEAEA] rounded-lg flex items-center justify-center gap-2"
                        >
                          {uploadLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                          Upload Imagem
                        </button>
                        <p className="text-xs text-[#666] mt-1">ou cole URL abaixo</p>
                      </div>
                    </div>
                    <input type="text" value={formData.image_url || ''} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="w-full px-3 py-2 mt-2 bg-[#0F0F10] border border-[#2A2A2D] rounded-lg text-[#EAEAEA]" placeholder="https://..." />
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#0F0F10] rounded-lg">
                    <input type="checkbox" id="upsell_active" checked={formData.is_active !== false} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-5 h-5" />
                    <label htmlFor="upsell_active" className="text-[#EAEAEA]">Upsell ativo (mostrar no checkout)</label>
                  </div>
                </>
              )}

              {/* Messages */}
              {saveMessage.text && (
                <div className={`flex items-center gap-2 text-sm p-3 rounded-lg ${saveMessage.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
                  {saveMessage.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  {saveMessage.text}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-[#2A2A2D]">
              <button onClick={() => setShowModal(false)} className="px-4 py-2.5 text-[#B5B5B5] hover:text-[#EAEAEA]">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saveLoading} className="flex items-center gap-2 bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-6 py-2.5 rounded-lg font-bold disabled:opacity-50">
                {saveLoading ? <Loader className="h-5 w-5 animate-spin" /> : <><Save className="h-5 w-5" /> Salvar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
